import MyQuestionFilter from '../components/MyQuestionFilter';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { questionApi } from '../api/question.api';
import { adApi } from '../api/ad.api';
import { weightedShuffle } from '../util/weightedRandom';
import { GridSkeleton, SectionLoading } from '../components/ui/skeleton';
import { QuestionV2 } from '../types/Question.type';
import QuestionCardV2 from '../components/QuestionCardV2';
import AdCard from '../components/ad/AdCard';
import { ISlice } from '../types/Page.type';

export function MyQuestionPage() {
  /**
   * Variables
   */
  const observeRef = useRef<HTMLDivElement | null>(null);
  const PAGE_SIZE = 5;
  /**
   * States
   */
  const [filterType, setFilterType] = useState<'my' | 'participated'>('my');

  /**
   * APIs - 통합 API 사용
   */
  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteQuery<ISlice<QuestionV2>>({
    queryKey: ['questionList', filterType],
    queryFn: ({ pageParam = 0 }) => {
      const params = {
        page: pageParam as number,
        size: PAGE_SIZE,
        filterType: filterType,
      };
      return questionApi.getUnifiedQuestionList(params);
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage.hasNext ? allPages.length : undefined;
    },
    initialPageParam: 0,
  });
  useEffect(() => {
    if (!observeRef.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 1.0 },
    );
    observer.observe(observeRef.current);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);
  const questionList = data?.pages.flatMap(page => page.content) ?? [];
  const isInitialLoading = isFetching && !isFetchingNextPage;

  /** 내설문 피드 광고 조회 */
  const { data: adsData } = useQuery({
    queryKey: ['ads', 'MY_FEED'],
    queryFn: () => adApi.getAdsByPosition('MY_FEED').then(res => res.data),
    staleTime: 1000 * 60 * 5,
  });
  const feedAds = useMemo(() => {
    if (!adsData || adsData.length === 0) return [];
    return weightedShuffle(adsData);
  }, [adsData]);

  /**
   * Handlers
   */
  const handleParamChange = useCallback((filter: string) => {
    setFilterType(filter as 'my' | 'participated');
  }, []);
  return (
    <div>
      {/* 탭 필터 */}
      <div>
        <MyQuestionFilter handleParamChange={handleParamChange} />
      </div>
      {/* 설문 목록 부분 - 설문 리스트 & 로딩 */}
      <div>
        <SectionLoading isLoading={isInitialLoading} skeleton={
          <GridSkeleton count={6} columns={{ default: 1, md: 2, lg: 3 }} variant="card" />
        }>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {questionList.map((question: QuestionV2, index: number) => (
              <div key={question.questionId}>
                <QuestionCardV2 question={question} />
                {/* 설문 4개마다 광고 삽입 */}
                {(index + 1) % 4 === 0 && feedAds.length > 0 && (
                  <div className="mt-4">
                    <AdCard
                      ad={feedAds[Math.floor(index / 4) % feedAds.length]}
                      position="MY_FEED"
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </SectionLoading>
        <div ref={observeRef}
             style={{ height: '50px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          {isFetchingNextPage && <p>Loading more...</p>}
        </div>
      </div>
    </div>
  );
}
